import React from 'react';
import PropTypes from 'prop-types';
// material-ui
import { useTheme } from '@mui/material/styles';
import { Box, Button } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

// third-party
import { FormattedMessage } from 'react-intl';

// "FLUX'a geç" in the LEFT sidebar, under the menu (owner, 2026-10-29): "Doktor portalında flux sağda değil solda
// menü altında fluxa geç butonu olacak". Replaces the floating bottom-right pill the Django shell used to draw
// (frontend/templates/frontend/index.html, removed in the same change).
//
// The shell still decides WHETHER: it renders <meta name="orthero-flux-entry" content="..."> only when
// oem_entry_url is set (frontend/views.py _oem_entry -> oem_frontend.sso_bridge.oem_entry_url). There is
// deliberately NO role / localStorage check here: an admin using "login as" keeps the admin's userData in
// localStorage, and a clinic's role is 'clinic' -- the server already answered both, the same way the bridge will.
//
// Kept apart from FluxLoginButton.js (the pre-login "Sign in to FLUX" on the login card) so that working screen
// stays untouched; the URL reader and the glyph are small copies of the ones there.

export const FLUX_ENTRY_META = 'meta[name="orthero-flux-entry"]';
// Layout px of the footer under the menu. Sidebar/index.js takes it off the menu's scroll area so the menu and
// this footer together still fill the drawer exactly (desktop branch).
export const FLUX_ENTRY_HEIGHT = 72;
// Whitespace/control chars and backslashes are dropped or rewritten by the URL parser ('/\t/x', '/\x' -> '//x').
const UNSAFE_CHARS = /[\s\u0000-\u001f\u007f\\]/;

// Staff pages embed the portal in an iframe and the bridge answers X-Frame-Options: DENY, so no entry there --
// the same rule the pill's inline script had.
const insideFrame = () => {
    try {
        return window.self !== window.top;
    } catch (e) {
        return true;
    }
};

/** The FLUX entry URL, or null: inside an iframe or the shell drew no usable meta. */
export const fluxEntryUrl = () => {
    if (insideFrame()) return null;
    let raw = '';
    try {
        const meta = document.querySelector(FLUX_ENTRY_META);
        raw = ((meta && meta.getAttribute('content')) || '').trim();
    } catch (e) {
        return null;
    }
    if (!raw || UNSAFE_CHARS.test(raw)) return null;
    // Owner, 2026-11-05: the link is STATIC and ABSOLUTE -- https://flux.orthero.com.tr/oem --
    // whatever the shell named, root-relative bridge path OR the paired-host absolute URL
    // (both spell /oem/giris, and that path's edge routing is broken in production).
    // The meta's PRESENCE still decides whether the button is drawn at all; its value
    // is never used as the href.
    return 'https://flux.orthero.com.tr/oem';
};

// The FLUX glyph (same path as the login card's button and the old pill). Decoration only.
const FluxGlyph = () => (
    <svg width="18" height="18" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
        <path d="M2 14V6.5l4 2.5V6.5l4 2.5V2h4v12H2Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    </svg>
);

// ==============================|| SIDEBAR - SWITCH TO FLUX ||============================== //

/**
 * A full-width outlined button pinned under the menu list. It is a plain <a href> on purpose: a full-page GET to
 * the bridge in the same tab, exactly like the pill. Never a react-router Link / navigate(): the router basename
 * is 'new', so '/oem/giris' would become '/new/oem/giris' (an SPA 404).
 *
 * `sticky` keeps it at the bottom of the visible drawer when the drawer paper itself scrolls (the temporary
 * drawer below md, and the phone/tablet branch where the menu is not in its own scroll area).
 */
const FluxSidebarEntry = ({ url, sticky }) => {
    const theme = useTheme();

    return (
        <Box
            sx={{
                height: FLUX_ENTRY_HEIGHT,
                boxSizing: 'border-box',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                px: 2,
                borderTop: `1px solid ${theme.palette.divider}`,
                bgcolor: theme.palette.background.default,
                ...(sticky && { position: 'sticky', bottom: 0, zIndex: 1 })
            }}
        >
            <Button
                component="a"
                href={url}
                fullWidth
                size="large"
                variant="outlined"
                color="secondary"
                startIcon={<FluxGlyph />}
                endIcon={<ArrowForwardIcon fontSize="small" />}
                data-testid="sidebar-to-flux"
                sx={{ textTransform: 'none', fontWeight: 600 }}
            >
                <FormattedMessage id="Switch to FLUX" defaultMessage="Switch to FLUX" />
            </Button>
        </Box>
    );
};

FluxSidebarEntry.propTypes = {
    url: PropTypes.string.isRequired,
    sticky: PropTypes.bool
};

export default FluxSidebarEntry;
